import { ArrowDownOutlined } from "@ant-design/icons"
import { Button, Divider, Empty, Flex, Skeleton, Typography } from "antd"
import React, { useEffect, useLayoutEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { firstUnreadIndex } from "../helpers/conversation-labels.ts"
import { dayDivider } from "../helpers/format-timestamp.ts"
import type {
  Message,
  MessageAttachment,
  Participant,
  PendingMessage
} from "../types/messaging.types.ts"
import { MessageRow, PendingMessageRow } from "./MessageRow.tsx"

// Two messages from the same person within this many minutes read as one
// block: same column, no repeated avatar or name.
const GROUPING_MINUTES = 5

const isGrouped = (rows: Message[], index: number): boolean => {
  const previous = rows[index - 1]
  const current = rows[index]
  if (!previous || previous.sender?.id !== current.sender?.id) return false
  if (previous.is_deleted || current.is_deleted) return false
  const gap =
    new Date(current.created).getTime() - new Date(previous.created).getTime()
  return gap < GROUPING_MINUTES * 60_000
}

// How close to the bottom still counts as "reading the latest", so a couple of
// pixels of inertia does not switch the view into catch-up mode.
const BOTTOM_TOLERANCE_PX = 80

interface MessageHistoryProps {
  messages: Message[]
  pending: PendingMessage[]
  participants: Participant[]
  currentUserId: string | undefined
  unreadCount: number
  loading: boolean
  error: boolean
  hasMore: boolean
  searchTerm?: string
  highlightedMessageId: string | null
  canSaveToProjectFiles: boolean
  onLoadOlder: () => void
  onRetryLoad: () => void
  onReply: (message: Message) => void
  onEdit: (message: Message, body: string) => void
  onDelete: (message: Message) => void
  onJumpToMessage: (messageId: string) => void
  onSaveToProjectFiles: (attachment: MessageAttachment) => void
  onRetrySend: (pending: PendingMessage) => void
  onDiscardSend: (clientToken: string) => void
  /** Called with the newest message the reader has actually reached. */
  onReachedMessage: (message: Message) => void
}

export function MessageHistory({
  messages,
  pending,
  participants,
  currentUserId,
  unreadCount,
  loading,
  error,
  hasMore,
  searchTerm,
  highlightedMessageId,
  canSaveToProjectFiles,
  onLoadOlder,
  onRetryLoad,
  onReply,
  onEdit,
  onDelete,
  onJumpToMessage,
  onSaveToProjectFiles,
  onRetrySend,
  onDiscardSend,
  onReachedMessage
}: MessageHistoryProps) {
  const { t } = useTranslation()
  const scrollRef = useRef<HTMLDivElement>(null)
  const [atBottom, setAtBottom] = useState(true)
  const [newBelow, setNewBelow] = useState(0)
  const lastCountRef = useRef(messages.length)

  const isAtBottom = () => {
    const node = scrollRef.current
    if (!node) return true
    return (
      node.scrollHeight - node.scrollTop - node.clientHeight <
      BOTTOM_TOLERANCE_PX
    )
  }

  // A new message must never yank the view away from someone reading older
  // history — it offers a button instead, and only auto-scrolls when they were
  // already at the bottom.
  useLayoutEffect(() => {
    const grew = messages.length > lastCountRef.current
    lastCountRef.current = messages.length
    if (!grew) return
    if (atBottom) {
      scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight })
    } else {
      setNewBelow((count) => count + 1)
    }
  }, [messages.length, atBottom])

  useEffect(() => {
    if (!highlightedMessageId) return
    document
      .getElementById(`message-${highlightedMessageId}`)
      ?.scrollIntoView({ block: "center", behavior: "smooth" })
  }, [highlightedMessageId])

  // Reaching the bottom is what marks messages read — delivery alone never
  // does, which is the whole point of reporting a position rather than
  // inferring one from the fetch.
  useEffect(() => {
    if (!atBottom) return
    const newest = messages[messages.length - 1]
    if (newest) onReachedMessage(newest)
  }, [atBottom, messages, onReachedMessage])

  const handleScroll = () => {
    const node = scrollRef.current
    if (!node) return
    const bottom = isAtBottom()
    setAtBottom(bottom)
    if (bottom) setNewBelow(0)
    if (node.scrollTop < 40 && hasMore && !loading) onLoadOlder()
  }

  const jumpToBottom = () => {
    const node = scrollRef.current
    if (!node) return
    node.scrollTo({ top: node.scrollHeight, behavior: "smooth" })
    setNewBelow(0)
  }

  const unreadStart = firstUnreadIndex(
    messages.map((row) => row.id),
    unreadCount
  )

  if (loading && messages.length === 0) {
    return (
      <div className={"flex-1 space-y-4 overflow-y-auto p-4"}>
        <Skeleton avatar active paragraph={{ rows: 2 }} />
        <Skeleton avatar active paragraph={{ rows: 1 }} />
        <Skeleton avatar active paragraph={{ rows: 3 }} />
      </div>
    )
  }

  if (error) {
    return (
      <Flex vertical align={"center"} justify={"center"} gap={"small"} flex={1}>
        <Typography.Text strong>
          {t("messages.errors.historyTitle", "Unable to load this conversation")}
        </Typography.Text>
        <Typography.Text type={"secondary"} className={"max-w-sm text-center"}>
          {t(
            "messages.errors.historyBody",
            "Something went wrong while fetching the messages. Please check your connection and try again."
          )}
        </Typography.Text>
        <Button onClick={onRetryLoad}>{t("common.retry", "Retry")}</Button>
      </Flex>
    )
  }

  let lastDay = ""

  return (
    <div className={"relative min-h-0 min-w-0 flex-1"}>
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        data-testid={"message-history"}
        className={"h-full overflow-y-auto pb-3"}
      >
        {hasMore && (
          <Flex justify={"center"} className={"py-2"}>
            <Button size={"small"} type={"link"} onClick={onLoadOlder}>
              {t("messages.loadOlder", "Load earlier messages")}
            </Button>
          </Flex>
        )}

        {messages.length === 0 && pending.length === 0 && (
          <Empty
            className={"py-16"}
            description={t(
              "messages.emptyConversation",
              "No messages yet. Say hello."
            )}
          />
        )}

        {messages.map((message, index) => {
          const day = dayDivider(message.created)
          const showDay = day !== lastDay
          lastDay = day
          return (
            <React.Fragment key={message.id}>
              {showDay && (
                <Divider plain className={"!my-3 !text-[11px] !text-slate-400"}>
                  {day}
                </Divider>
              )}
              {index === unreadStart && (
                <Divider
                  plain
                  data-testid={"unread-divider"}
                  className={"!my-2 text-xs !text-blue-600 !border-blue-200"}
                >
                  {t("messages.unreadDivider", {
                    count: unreadCount,
                    defaultValue_one: "{{count}} unread message",
                    defaultValue_other: "{{count}} unread messages"
                  })}
                </Divider>
              )}
              <MessageRow
                message={message}
                participants={participants}
                isOwn={message.sender?.id === currentUserId}
                grouped={isGrouped(messages, index)}
                isHighlighted={message.id === highlightedMessageId}
                searchTerm={searchTerm}
                canSaveToProjectFiles={canSaveToProjectFiles}
                onReply={onReply}
                onEdit={onEdit}
                onDelete={onDelete}
                onJumpToMessage={onJumpToMessage}
                onSaveToProjectFiles={onSaveToProjectFiles}
              />
            </React.Fragment>
          )
        })}

        {pending.map((row) => (
          <PendingMessageRow
            key={row.client_token}
            pending={row}
            onRetry={onRetrySend}
            onDiscard={onDiscardSend}
          />
        ))}
      </div>

      {newBelow > 0 && (
        <Button
          size={"small"}
          type={"primary"}
          icon={<ArrowDownOutlined />}
          onClick={jumpToBottom}
          data-testid={"new-messages-button"}
          className={"absolute bottom-4 left-1/2 -translate-x-1/2 shadow-md"}
        >
          {t("messages.newMessagesBelow", "New messages")}
        </Button>
      )}
    </div>
  )
}
